import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CareStateProposedActionT,
  CareStateT,
  PrescriptionCancellationRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { useCareStateCommands } from '@/entities/care-state-domain';
import { extractApiErrorDetail } from '@/shared/lib/errors/extractApiErrorDetail';

import { createPrescriptionCancellationApi } from './api';

export type CancellationTarget =
  | { prescriptionRecordId: string }
  | { dosespotPrescriptionId: string }
  | { proposedActionExternalId: string };

export function isPrescriptionWorkflowAction(action: CareStateProposedActionT): boolean {
  return (
    action.payload.tool_name === 'draft_prescription' ||
    typeof action.payload.tool_input.priorAuthRecordId === 'string'
  );
}

export function hasUnfinishedRequestActions(
  state: CareStateT,
  requestId: string,
  archivedId?: string
) {
  return state.proposed_actions.some(
    (action) =>
      action.link.type === 'request' &&
      action.link.id === requestId &&
      action.external_id !== archivedId &&
      action.todo_status === 'Open' &&
      action.run_status !== 'Succeeded' &&
      action.decision !== 'Rejected'
  );
}

export function presentCancellation(result: PrescriptionCancellationRequestT) {
  if (!result.prescription || !result.prior_auth) {
    throw new Error('Cancellation response is missing prescription or PA progress.');
  }
  const steps = [result.prescription, result.prior_auth];
  // Mirrors the backend's `complete`: only outcomes decide it. Errors are still
  // shown in the description.
  const complete =
    result.prescription.outcome === 'confirmed' &&
    ['confirmed', 'not_needed'].includes(result.prior_auth.outcome ?? '');
  const description = steps
    .map(
      (s, i) =>
        `${i === 0 ? 'Prescription' : 'PA'}: ${s.status ?? s.outcome}${s.error ? ` — ${s.error}` : ''}`
    )
    .join('. ');
  if (complete) {
    toast.success('Cancellation confirmed.', { description });
  } else if (steps.some((s) => s.error || s.outcome === 'refused' || s.outcome === 'unconfirmed')) {
    toast.error('Cancellation needs follow-up.', { description });
  } else {
    toast.info('Cancellation requested; confirmation pending.', { description });
  }
  return complete;
}

export function usePrescriptionCancellation(patientUserId: string) {
  const queryClient = useQueryClient();
  const commands = useCareStateCommands();
  const closeFinishedRequest = (state: CareStateT, requestId: string, archivedId?: string) => {
    const unfinished = hasUnfinishedRequestActions(state, requestId, archivedId);
    const request = state.requests.find((r) => r.id === requestId);
    if (!unfinished && request && ['Open', 'Pending Follow Up'].includes(request.status)) {
      commands.execute(
        { type: 'request.setStatus', requestId, status: 'Cancelled' },
        { actor: 'clinician' }
      );
    }
  };
  const busy = useRef(false);
  const [isPending, setPending] = useState(false);
  const cancel = async (target: CancellationTarget): Promise<'confirmed' | 'requested' | false> => {
    if (busy.current) {
      return false;
    }
    busy.current = true;
    setPending(true);
    try {
      const api = await createPrescriptionCancellationApi();
      let recordId: string | undefined;
      let proposal: CareStateProposedActionT | undefined;
      if ('prescriptionRecordId' in target) {
        recordId = target.prescriptionRecordId;
      } else {
        const state = await api.fetchState(patientUserId);
        let prescriptions = state.prescriptions;
        if ('dosespotPrescriptionId' in target) {
          prescriptions = prescriptions.filter(
            (rx) => rx.dosespot_prescription_id === target.dosespotPrescriptionId
          );
        } else {
          proposal = state.proposed_actions.find(
            (action) => action.external_id === target.proposedActionExternalId
          );
          if (!proposal || !isPrescriptionWorkflowAction(proposal)) {
            throw new Error('Prescription proposal not found. Refresh the patient and try again.');
          }
          if (proposal.run_status === 'Running') {
            throw new Error('This action is in progress. Wait for its outcome.');
          }
          const paId = proposal.payload.tool_input.priorAuthRecordId;
          const rxId =
            proposal.tool_output?.dosespotPrescriptionId ??
            proposal.payload.tool_input.dosespotPrescriptionId;
          if (typeof paId === 'string') {
            recordId = await api.getPriorAuthPrescriptionRecordId(paId);
          }
          prescriptions = prescriptions.filter((rx) =>
            typeof rxId === 'string'
              ? rx.dosespot_prescription_id === rxId
              : proposal?.link.type === 'request' &&
                rx.link.type === 'request' &&
                rx.link.id === proposal.link.id
          );
          if (!recordId && typeof rxId !== 'string' && proposal.link.type === 'request') {
            const requestId = proposal.link.id;
            const linked = new Set(
              state.prior_auth_requests
                .filter((pa) => pa.link.type === 'request' && pa.link.id === requestId)
                .map((pa) => pa.prescription_record_id)
            );
            if (linked.size > 1) {
              throw new Error(
                'Multiple prescriptions are linked. Cancel the exact order in Rx Orders.'
              );
            }
            recordId = [...linked][0];
          }
          if (!recordId && prescriptions.length === 0) {
            if (
              rxId ||
              proposal.payload.tool_name !== 'draft_prescription' ||
              proposal.run_status !== 'Idle'
            ) {
              throw new Error('The stored prescription could not be resolved. Check Rx Orders.');
            }
            const result = await api.archiveProposal(patientUserId, proposal.external_id);
            if (result.errors.length) {
              throw new Error(result.errors.map((error) => error.message).join('. '));
            }
            if (proposal.link.type === 'request') {
              closeFinishedRequest(state, proposal.link.id, proposal.external_id);
            }
            toast.success('Prescription proposal cancelled.');
            return 'confirmed';
          }
        }
        if (!recordId) {
          const targets = new Set(prescriptions.map((rx) => rx.prescription_record_id));
          if (targets.size !== 1) {
            throw new Error('The exact prescription could not be resolved. Check Rx Orders.');
          }
          recordId = [...targets][0]!;
        }
      }
      const confirmed = presentCancellation(await api.cancel(recordId));
      if (confirmed) {
        const state = await api.fetchState(patientUserId);
        const linked = [...state.prescriptions, ...state.prior_auth_requests].filter(
          (row) => row.prescription_record_id === recordId
        );
        for (const row of linked) {
          if (row.link.type === 'request') {
            closeFinishedRequest(state, row.link.id);
          }
        }
      }
      return confirmed ? 'confirmed' : 'requested';
    } catch (error) {
      toast.error('Could not cancel prescription.', { description: extractApiErrorDetail(error) });
      return false;
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rx'] }),
        queryClient.invalidateQueries({ queryKey: ['priorAuth'] }),
      ]);
      busy.current = false;
      setPending(false);
    }
  };
  return { cancel, isPending };
}
